import { useAppDispatch } from 'app/store';
import { useEffect, useState } from 'react';
import useReactWebSocket, { ReadyState } from 'react-use-websocket';
import { WebSocketEventCode } from 'app/utils/hooks/types';
import {
  handleWebSocketMessage,
  setIsWebSocketLive,
} from 'app/store/WebSockets/slice';
import { useWebSocketConnectionLink } from 'app/utils/hooks/webSockets/useWebSocketConnectionLink';
import { useKeepWebSocketConnectionLive } from 'app/utils/hooks/webSockets/useKeepWebSocketConnectionLive';
import { useActivityPing } from 'app/utils/hooks/webSockets/useActivityPing';

export const useAppWebSocket = () => {
  useActivityPing();
  const dispatch = useAppDispatch();
  const {
    connectionUrl,
    setConnectionUrl,
    refetchConnectionUrl,
  } = useWebSocketConnectionLink({ shouldFetchSignedConnectionUrl: true });
  const [lastMessageTimestamp, setLastMessageTimeStamp] = useState<number>(
    new Date().getTime()
  );

  const { readyState, sendJsonMessage } = useReactWebSocket(connectionUrl, {
    share: true,
    retryOnError: true,
    reconnectAttempts: 3,
    reconnectInterval: 5000,
    shouldReconnect: (event) =>
      event.code !== WebSocketEventCode.NORMAL_CLOSURE,
    onMessage: (message) => {
      setLastMessageTimeStamp(new Date().getTime());
      dispatch(handleWebSocketMessage(JSON.parse(message.data)));
    },
    onClose: (event) => {
      if (event.code === WebSocketEventCode.GOING_AWAY) {
        refetchConnectionUrl();
      }
    },
  });

  useKeepWebSocketConnectionLive({
    lastMessageTimestamp,
    sendJsonMessage,
    readyState,
    reconnect: () => {
      setConnectionUrl(null);
      setLastMessageTimeStamp(new Date().getTime());
      refetchConnectionUrl();
    },
  });

  useEffect(() => {
    dispatch(setIsWebSocketLive({ isLive: readyState === ReadyState.OPEN }));
  }, [dispatch, readyState]);

  useEffect(
    () =>
      function cleanup() {
        if (readyState === ReadyState.OPEN) {
          setConnectionUrl(null);
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [readyState]
  );
};
